<template>
  <v-card
    variant="outlined"
    class="mb-4 pa-4"
    :style="{ borderColor: borderColor || '#ccc' }"
  >
    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center gap-2">
        <v-chip :color="themeColor" size="small" variant="flat" class="text-white">
          {{ itemLabel }} #{{ index + 1 }}
        </v-chip>
        <v-chip v-if="itemData._syncedFromDirector" size="x-small" color="info" variant="tonal">
          {{ tl('Auto-synced') }}
        </v-chip>
      </div>
      <v-btn
        v-if="!readonly"
        icon
        variant="text"
        size="small"
        color="error"
        @click="emit('remove')"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </div>

    <!-- Header Fields (rendered before type selector, e.g., promoterName) -->
    <div v-if="headerFields && headerFields.length > 0" :class="[gridClass, 'mb-3']">
      <FieldRenderer
        v-for="field in headerFields"
        :key="'hf-' + field.id"
        :field="field"
        :model-value="getFieldValue(field.id)"
        :readonly="readonly"
        :ref-data="refData"
        :density="density"
        :contacts="contacts"
        :contacts-loading="contactsLoading"
        :contact-placeholder="contactPlaceholder"
        :contact-hint="contactHint"
        :error-message="itemFieldError(field.id)"
        :field-key="itemFieldKey(field.id)"
        @update:model-value="setFieldValue(field.id, $event)"
        @contact-change="emit('contact-change', $event)"
      />
    </div>

    <!-- Type Selector (for Shareholders) -->
    <div v-if="typeSelector" class="mb-3">
      <v-select
        :model-value="currentType"
        :items="typeSelectorItems"
        :label="tl(typeSelector.label)"
        :readonly="readonly"
        item-title="label"
        item-value="value"
        variant="outlined"
        :density="density"
        style="max-width: 300px"
        @update:model-value="handleTypeChange"
      />
    </div>

    <!-- Variant Fields (e.g., Individual or BodyCorporate groups) -->
    <div v-for="(group, gIdx) in currentVariantGroups" :key="'vg-' + gIdx">
      <div v-if="group.title" class="text-subtitle-2 font-weight-medium mt-3 mb-2"
        :class="group.style === 'highlight' ? 'text-primary' : ''"
      >
        {{ tl(group.title) }}
      </div>
      <div :class="gridClass">
        <template v-for="field in resolvedGroupFields(group)" :key="field.id">
          <FieldRenderer
            v-if="isFieldVisible(field)"
            :field="field"
            :model-value="getFieldValue(field.id)"
            :readonly="readonly"
            :ref-data="refData"
            :density="density"
            :country-value="getFieldValue('country')"
            :linked-field-value="getLinkedValue(field)"
            :entities="entities"
            :entities-loading="entitiesLoading"
            :contacts="contacts"
            :contacts-loading="contactsLoading"
            :contact-placeholder="contactPlaceholder"
            :contact-hint="contactHint"
            :date-picker-value="getDatePickerValue(field.id)"
            :error-message="itemFieldError(field.id)"
        :field-key="itemFieldKey(field.id)"
            @update:model-value="setFieldValue(field.id, $event)"
            @file-change="handleFileChange"
            @entity-change="emit('entity-change', $event)"
            @contact-change="emit('contact-change', $event)"
            @date-picker-change="emit('date-picker-change', $event)"
            @date-blur="emit('date-blur')"
          />
        </template>
      </div>
    </div>

    <!-- Non-variant Groups (groups on itemTemplate directly, e.g. Director) -->
    <div v-for="(group, gIdx) in directGroups" :key="'dg-' + gIdx">
      <div v-if="group.title" class="text-subtitle-2 font-weight-medium mt-3 mb-2">
        {{ tl(group.title) }}
      </div>
      <!-- Address group -->
      <AddressBlock
        v-if="group.ref === 'address'"
        :address-id="'item-' + index + '-addr'"
        :title="tl(group.title || 'Address')"
        :model-value="getAddressValue()"
        :include-optional="group.includeOptional"
        :readonly="readonly"
        :ref-data="refData"
        :density="density"
        :resolve-template-fn="resolveTemplateFn"
        :show-errors="showErrors"
        :error-prefix="`${sectionId}[${index}]._address.`"
        :field-errors="fieldErrors"
        :require-office-number="false"
        @update:model-value="setAddressValue($event)"
      />
      <!-- Regular fields -->
      <div v-else class="field-grid">
        <template v-for="field in resolvedGroupFields(group)" :key="field.id">
          <FieldRenderer
            v-if="isFieldVisible(field)"
            :field="field"
            :model-value="getFieldValue(field.id)"
            :readonly="readonly"
            :ref-data="refData"
            :density="density"
            :country-value="getFieldValue('country')"
            :linked-field-value="getLinkedValue(field)"
            :entities="entities"
            :entities-loading="entitiesLoading"
            :contacts="contacts"
            :contacts-loading="contactsLoading"
            :contact-placeholder="contactPlaceholder"
            :contact-hint="contactHint"
            :date-picker-value="getDatePickerValue(field.id)"
            :error-message="itemFieldError(field.id)"
        :field-key="itemFieldKey(field.id)"
            @update:model-value="setFieldValue(field.id, $event)"
            @file-change="handleFileChange"
            @entity-change="emit('entity-change', $event)"
            @contact-change="emit('contact-change', $event)"
            @date-picker-change="emit('date-picker-change', $event)"
            @date-blur="emit('date-blur')"
          />
        </template>
      </div>
    </div>

    <!-- Common Groups (Share Info, Address for shareholders) -->
    <div v-for="(group, gIdx) in commonGroups" :key="'cg-' + gIdx">
      <div v-if="group.title" class="text-subtitle-2 font-weight-medium mt-3 mb-2">
        {{ tl(group.title) }}
      </div>
      <AddressBlock
        v-if="group.ref === 'address'"
        :address-id="'item-' + index + '-commonAddr'"
        :title="tl(group.title || 'Address')"
        :model-value="getAddressValue()"
        :include-optional="group.includeOptional"
        :readonly="readonly"
        :ref-data="refData"
        :density="density"
        :resolve-template-fn="resolveTemplateFn"
        :show-errors="showErrors"
        :error-prefix="`${sectionId}[${index}]._address.`"
        :field-errors="fieldErrors"
        :require-office-number="false"
        @update:model-value="setAddressValue($event)"
      />
      <div v-else :class="gridClass">
        <FieldRenderer
          v-for="field in resolvedGroupFields(group)"
          :key="field.id"
          :field="field"
          :model-value="getFieldValue(field.id)"
          :readonly="readonly"
          :ref-data="refData"
          :density="density"
          :error-message="itemFieldError(field.id)"
        :field-key="itemFieldKey(field.id)"
          @update:model-value="setFieldValue(field.id, $event)"
        />
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import FieldRenderer from './FieldRenderer.vue';
import AddressBlock from './AddressBlock.vue';
import type { FieldDef, TemplateGroup, RefDataItem } from '../composables/useFormRenderer';

// Translation helper
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface Props {
  index: number;
  sectionId?: string;
  itemData: Record<string, any>;
  itemLabel: string;
  borderColor?: string;
  themeColor?: string;
  readonly?: boolean;
  refData?: Record<string, RefDataItem[]>;
  density?: 'compact' | 'comfortable';
  // Header fields (rendered before type selector, e.g., promoterName)
  headerFields?: FieldDef[];
  // Type selector config (for shareholders)
  typeSelector?: FieldDef | null;
  // Variant groups by type
  variants?: Record<string, { groups: TemplateGroup[] }> | null;
  // Direct groups (for directors - no type selector)
  groups?: TemplateGroup[];
  // Common groups (share info, address)
  commonGroups?: TemplateGroup[];
  // Template resolve function from parent
  resolveTemplateFn?: (ref: string, includeOptional?: boolean) => FieldDef[];
  resolveGroupFn?: (group: TemplateGroup) => FieldDef[];
  // Show address validation errors
  showErrors?: boolean;
  fieldErrors?: Record<string, string>;
  // RN-specific props (entity/contact search)
  entities?: any[];
  entitiesLoading?: boolean;
  contacts?: any[];
  contactsLoading?: boolean;
  contactPlaceholder?: string;
  contactHint?: string;
}

const props = withDefaults(defineProps<Props>(), {
  borderColor: '#ccc',
  themeColor: '#1565c0',
  readonly: false,
  refData: () => ({}),
  density: 'compact',
  headerFields: () => [],
  typeSelector: null,
  variants: null,
  groups: () => [],
  commonGroups: () => [],
  resolveTemplateFn: undefined,
  resolveGroupFn: undefined,
  showErrors: false,
  fieldErrors: () => ({}),
  sectionId: '',
  entities: () => [],
  entitiesLoading: false,
  contacts: () => [],
  contactsLoading: false,
  contactPlaceholder: '',
  contactHint: '',
});

const emit = defineEmits<{
  'update:item': [data: Record<string, any>];
  remove: [];
  'member-toggle': [isAlsoMember: boolean];
  'entity-change': [entityCd: string | null];
  'contact-change': [value: any];
  'date-picker-change': [value: any];
  'date-blur': [];
}>();

// Grid class: 2 columns for comfortable density, 3 columns for compact
const gridClass = computed(() => props.density === 'comfortable' ? 'field-grid field-grid-2col' : 'field-grid');

function itemFieldError(fieldId: string): string | undefined {
  if (!props.showErrors || !props.sectionId) return undefined;
  return props.fieldErrors?.[`${props.sectionId}[${props.index}].${fieldId}`];
}

function itemFieldKey(fieldId: string): string {
  return `${props.sectionId}[${props.index}].${fieldId}`;
}

// Use typeSelector.id to determine which field holds the type value
const typeFieldId = computed(() => props.typeSelector?.id || 'type');
const currentType = computed(() => props.itemData[typeFieldId.value] || props.typeSelector?.default || 'Individual');

// Type selector items
// Type selector options are code data (e.g., "Individual", "Body Corporate") - do not translate
const typeSelectorItems = computed(() => {
  if (!props.typeSelector) return [];
  return props.typeSelector.options || [];
});

// Variant groups for current type (shareholders)
const currentVariantGroups = computed((): TemplateGroup[] => {
  if (!props.variants) return [];
  const variant = props.variants[currentType.value];
  return variant?.groups || [];
});

// Direct groups (no type selector, e.g. director)
const directGroups = computed((): TemplateGroup[] => {
  if (props.typeSelector) return [];
  return props.groups || [];
});

function resolvedGroupFields(group: TemplateGroup): FieldDef[] {
  if (props.resolveGroupFn) return props.resolveGroupFn(group);
  if (group.fields) return group.fields;
  return [];
}

/**
 * showWhen 한 조건 평가. SectionRenderer 와 같은 규칙을 쓴다.
 * 예전에는 이 목록(이사/주주)만 단일 조건만 보고 `all` 과 `falsy` 를 무시해서,
 * "여권 없음"을 체크해도 2차 신분증 칸이 그대로 남아 있었다.
 */
function evalShowWhenCondition(cond: { field?: string; values?: string[]; value?: string; notEmpty?: boolean; falsy?: boolean }): boolean {
  if (!cond.field) return true;
  const val = getFieldValue(cond.field);
  const strVal = String(val ?? '');
  if (cond.falsy) return !val || strVal === 'false' || strVal === '0';
  if (cond.notEmpty) return strVal !== '';
  if (cond.values) return cond.values.includes(strVal) || cond.values.includes(val);
  if (cond.value) return strVal === String(cond.value);
  return true;
}

function isFieldVisible(field: FieldDef): boolean {
  // Hide the type field from variant groups if typeSelector handles it
  if (props.typeSelector && field.id === typeFieldId.value) return false;
  if (!field.showWhen) return true;
  if (field.showWhen.all?.length) return field.showWhen.all.every(evalShowWhenCondition);
  return evalShowWhenCondition(field.showWhen);
}

function getFieldValue(fieldId: string): any {
  return props.itemData[fieldId] ?? '';
}

function setFieldValue(fieldId: string, value: any) {
  const updated = { ...props.itemData, [fieldId]: value };
  emit('update:item', updated);

  // Handle isAlsoMember toggle for directors
  if (fieldId === 'isAlsoMember') {
    emit('member-toggle', !!value);
  }
}

function getAddressValue(): Record<string, any> {
  return props.itemData._address || {};
}

function setAddressValue(addr: Record<string, any>) {
  const updated = { ...props.itemData, _address: addr };
  emit('update:item', updated);
}

function getLinkedValue(field: FieldDef): string {
  if (!field.dynamicLabel) return '';
  return getFieldValue(field.dynamicLabel.linkedField);
}

function getDatePickerValue(fieldId: string): Date | null {
  return props.itemData[`${fieldId}_pickerValue`] || props.itemData.datePickerValue || null;
}

function handleTypeChange(newType: string) {
  const updated = { ...props.itemData, [typeFieldId.value]: newType };
  emit('update:item', updated);
}

function handleFileChange(payload: { fieldId: string; file: File | null }) {
  const files = { ...(props.itemData._files || {}), [payload.fieldId]: payload.file };
  const updated = { ...props.itemData, _files: files };
  emit('update:item', updated);
}
</script>

<style scoped>
.field-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 12px;
}

.field-grid.field-grid-2col {
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 960px) {
  .field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
